/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/StockSearch.tsx
 * ----------------------------------------------------------------------------
 * Consulta de estoque (server-side) com:
 *  - Autodetector de endpoint (resolveStockEndpoint)
 *  - Busca + paginação DRF (funciona também com array simples)
 *  - Ordenação no servidor (combobox + clique no cabeçalho)
 *  - Tabela com Column Manager e cabeçalho clicável
 *
 * Notas de manutenção:
 *  - Se o backend mudar o nome do campo de quantidade, ajuste os candidatos
 *    no NORMALIZADOR (ver normalizeStockRow).
 *  - Se a ordenação do backend usar nomes diferentes (ex.: qty_on_hand),
 *    mapeie em ORDERING_MAP.
 *  - Se houver variação no shape do retorno (uom como string/object), o
 *    "pick" já trata, mas deixe exemplos no comentário para o próximo dev.
 * ========================================================================== */

import { useEffect, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import DataTable, { Column, SortDir } from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import { http, resolveStockEndpoint } from '@/lib/http'

/* ----------------------------------------------------------------------------
 * Utils de leitura tolerante (get/pick) e formatação
 * --------------------------------------------------------------------------*/
function get(obj: any, path: string): any {
    if (!obj) return undefined
    return path.split('.').reduce((acc: any, k: string) => (acc == null ? acc : acc[k]), obj)
}
function pick(obj: any, ...candidates: string[]): any {
    for (const p of candidates) {
        const v = p.includes('.') ? get(obj, p) : obj?.[p]
        if (v !== undefined && v !== null && v !== '') return v
    }
    return undefined
}
const nf = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 })
const fmtQty = (v?: number) => (typeof v === 'number' ? nf.format(v) : '')
const fmtDate = (s?: string) => {
    if (!s) return ''
    const d = new Date(s)
    return isNaN(+d) ? s : d.toLocaleDateString()
}

/* ----------------------------------------------------------------------------
 * Normalizador de linhas vindas do backend
 *  - Garante "qty" numérico
 *  - Resolve variações de nomes/formatos (qty, quantity, on_hand, strings)
 *  - Resolve uom string vs objeto
 *  - Resolve localização/produto em caminhos diferentes
 * --------------------------------------------------------------------------*/
type AnyRow = Record<string, any>

/** Converte "1.234,56" / "1,234.56" / "1234,56" / "1234.56" → number */
function toNumberLoose(v: any): number | undefined {
    if (v === undefined || v === null || v === '') return undefined
    if (typeof v === 'number') return v
    if (typeof v !== 'string') {
        const n = Number(v)
        return isFinite(n) ? n : undefined
    }
    // remove espaços
    let s = v.trim()
    // se tem vírgula e ponto, assume vírgula como decimal quando ela vem por último
    if (s.includes(',') && s.includes('.')) {
        if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
            s = s.replace(/\./g, '').replace(',', '.')
        } else {
            s = s.replace(/,/g, '')
        }
    } else if (s.includes(',')) {
        // apenas vírgula: trata como decimal
        s = s.replace(/\./g, '').replace(',', '.')
    } else {
        // apenas ponto: remove separador de milhar
        const parts = s.split('.')
        if (parts.length > 2) s = parts.join('') // "1.234.567" -> "1234567"
    }
    const n = Number(s)
    return isFinite(n) ? n : undefined
}

function normalizeStockRow(r: AnyRow) {
    const qtyCandidate = pick(r, 'qty', 'quantity', 'qty_on_hand', 'on_hand', 'onhand', 'balance')
    const qty = toNumberLoose(qtyCandidate)

    return {
        id: r.id,
        product_code: pick(r, 'product.code', 'product_code'),
        product_name: pick(r, 'product.name1', 'product.name', 'product_name', 'description'),
        location_code: pick(r, 'location.code', 'location_code', 'location'),
        lot: pick(r, 'lot', 'batch', 'lot_code') as string,
        pallet_code: pick(r, 'pallet_code', 'pallet') as string,
        qty: qty ?? 0, // sempre número para a tabela/soma
        uom: pick(r, 'uom.code', 'uom_code', 'uom') as string,
        expiry_date: (pick(r, 'expiration_date', 'expiry_date', 'validity') as string) || undefined,
        owner_name: pick(r, 'owner.name', 'owner', 'owner_name') as string,
    }
}

/* Campo visual -> campo do backend (ordenacao DRF) */
const ORDERING_MAP: Record<string, string> = {
    product_code: 'product__code',
    product_name: 'product__name1', // ajuste caso o backend mude
    location_code: 'location__code',
    qty: 'qty', // mude para 'quantity' se o backend usar outro nome
    expiry_date: 'expiration_date',
    pallet_code: 'pallet_code',
    lot: 'lot',
    uom: 'uom', // 'uom__code' se campo for related no backend
    owner_name: 'owner__name',
}

type SortState = { key?: string; dir?: SortDir }

export default function StockSearch() {
    const [q, setQ] = useState('')
    const [ordering, setOrdering] = useState<string>('') // string DRF, ex.: "-qty"
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(20)

    // filtros mudaram? volte para página 1
    useEffect(() => setPage(1), [q, ordering, pageSize])

    const { data, isFetching, error } = useQuery({
        queryKey: ['stock', q, ordering, page, pageSize],
        queryFn: async () => {
            const ep = await resolveStockEndpoint()

            // Atenção: alguns backends usam "q", outros "search".
            // Mandamos os dois; o backend ignora o que não usa.
            const params: Record<string, any> = { page, page_size: pageSize }
            if (q) {
                params.search = q
                params.q = q
            }
            if (ordering) params.ordering = ordering

            const resp = await http.get(ep, { params })
            const d = resp.data

            // DRF paginado
            if (d && Array.isArray(d.results)) return d
            // Array simples
            if (Array.isArray(d)) return { count: d.length, results: d }
            // Fallback seguro
            return { count: Number(d?.count ?? 0) || 0, results: d?.results ?? [] }
        },
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    })

    // Normaliza linhas do backend para a tabela
    const rows = useMemo(
        () => (data?.results ?? []).map(normalizeStockRow),
        [data],
    )

    // Estado visual de ordenação (espelha a string DRF -> estado da tabela)
    const sortState: SortState = useMemo(() => {
        if (!ordering) return {}
        const dir: SortDir = ordering.startsWith('-') ? 'desc' : 'asc'
        const field = ordering.replace(/^-/, '')
        const key = Object.keys(ORDERING_MAP).find((k) => ORDERING_MAP[k] === field)
        return key ? { key, dir } : {}
    }, [ordering])

    // Clique no cabeçalho -> atualiza ordering DRF
    const onSortChange = (s: SortState) => {
        if (!s.key || !s.dir) {
            setOrdering('')
            return
        }
        const field = ORDERING_MAP[s.key] || s.key
        setOrdering((s.dir === 'desc' ? '-' : '') + field)
    }

    // Colunas da tabela (Column Manager ligado)
    const columns: Column<(typeof rows)[number]>[] = [
        { key: 'location_code', header: 'Local', noWrap: true, sortable: true, sortField: ORDERING_MAP.location_code },
        { key: 'product_code', header: 'Produto', noWrap: true, sortable: true, sortField: ORDERING_MAP.product_code },
        { key: 'product_name', header: 'Descrição', className: 'min-w-[220px]', sortable: true, sortField: ORDERING_MAP.product_name },
        { key: 'lot', header: 'Lote', noWrap: true, sortable: true },
        { key: 'pallet_code', header: 'Pallet', noWrap: true, sortable: true },
        {
            key: 'qty',
            header: 'Qtd',
            align: 'right',
            sortable: true,
            sortField: ORDERING_MAP.qty,
            render: (r) => fmtQty(r.qty),
        },
        { key: 'uom', header: 'UM', noWrap: true, sortable: true, sortField: ORDERING_MAP.uom },
        { key: 'expiry_date', header: 'Validade', sortable: true, sortField: ORDERING_MAP.expiry_date, render: (r) => fmtDate(r.expiry_date) },
        { key: 'owner_name', header: 'Owner', sortable: true, sortField: ORDERING_MAP.owner_name },
    ]

    return (
        <div className="grid gap-4">
            <div className="card p-4">
                <h2 className="text-lg font-semibold">Consulta de Estoque</h2>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_200px_140px] gap-3">
                    <input
                        className="input"
                        placeholder="Produto, localização, lote, pallet..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />

                    {/* Ordenação no servidor: opções mais comuns já mapeadas em ORDERING_MAP */}
                    <select
                        className="input"
                        value={ordering}
                        onChange={(e) => setOrdering(e.target.value)}
                        title="Ordenação (servidor)"
                    >
                        <option value="">Ordenação</option>
                        <option value="-qty">Qtd (maior→menor)</option>
                        <option value="qty">Qtd (menor→maior)</option>
                        <option value="product__code">Produto (A→Z)</option>
                        <option value="-product__code">Produto (Z→A)</option>
                        <option value="location__code">Local (A→Z)</option>
                        <option value="-location__code">Local (Z→A)</option>
                        <option value="-expiration_date">Validade (mais recente)</option>
                        <option value="expiration_date">Validade (mais antiga)</option>
                    </select>

                    <select className="input" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                        {[10, 20, 50, 100].map((s) => (
                            <option key={s} value={s}>
                                {s} por página
                            </option>
                        ))}
                    </select>
                </div>

                {error && (
                    <div className="mt-2 text-sm text-red-600">
                        Erro ao consultar estoque: {String((error as any)?.message || '—')}
                    </div>
                )}
            </div>

            <div className="card p-4">
                <DataTable
                    id="stock-table"
                    columns={columns}
                    data={rows}
                    loading={isFetching}
                    enableColumnManager
                    sort={sortState}
                    onSortChange={onSortChange}
                    initialHidden={[]}
                />
                <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm text-neutral-500">
                        {typeof data?.count === 'number' ? (
                            <span>
                                Total: <b>{data.count}</b> itens
                            </span>
                        ) : (
                            '—'
                        )}
                    </div>
                    <Pagination page={page} pageSize={pageSize} total={data?.count ?? 0} onPageChange={setPage} />
                </div>
            </div>
        </div>
    )
}
